import {
  Countries,
  TypeOfPublicArea,
} from '@bupa-monorepo/shared-types/nationality-types';
import { Order } from '@bupa-monorepo/shared-types/query-types';

export interface SingleMemberFirmAddress {
  country?: Countries | null;
  postalCode?: string | null;
  city?: string | null;
  typeOfPublicArea?: TypeOfPublicArea | null;
  nameOfPublicArea?: string | null;
  houseNumber?: string | null;
  building?: string | null;
  staircase?: string | null;
  floor?: string | null;
  door?: string | null;
  description?: string | null;
}

export enum SingleMemberFirmPricingPackage {
  BASIC = 'BASIC',
  EXTENDED = 'EXTENDED',
}

export enum SingleMemberFirmTypesOfTaxation {
  KATA = 'KATA',
  VSZJA = 'VSZJA',
  ATALANYADO = 'Átalányadó',
  KIVA = 'KIVA',
  TAO = 'TAO',
}

export enum SingleMemberFirmFieldsOfWork {
  informatika = 'Informatika',
  epitoipar = 'Építőipar',
  e_kereskedelem = 'E-kereskedelem',
  kis_es_nagy_kereskedelem = 'Kis- és nagykereskedelem',
  muveszet = 'Művészet',
  oktatas_kutatas = 'Oktatás, kutatás',
  szallitas_raktarozas = 'Szállítás, raktározás',
  szepsegipar = 'Szépségipar',
  egeszsegugy = 'Egészségügy',
  szallas_vendeglatas = 'Szállás, vendéglátás',
  gyartas_es_feldolgozoipar = 'Gyártás és feldolgozóipar',
  penzugy_biztositas = 'Pénzügy, biztosítás',
  mezo_es_erdogazdalgodas_halaszat = 'Mező- és erdőgazdálkodás, halászat',
  start_up = 'Start up',
  gepjarmu_ipar = 'Gépjármű ipar',
  ingatlankereskedelem = 'Ingatlankereskedelem',
  futar = 'Futár',
  ugyvedi_iroda = 'Ügyvédi iroda',
  egyeb = 'Egyéb',
}

export enum SingleMemberFirmCreationType {
  SOLE_PROPRIETORSHIP = 'soleProprietorship',
  SINGLE_MEMBER_LIMITED_LIABILITY_COMPANY = 'singleMemberLimitedLiabilityCompany',
}

export enum SingleMemberFirmCreationStatus {
  DRAFT = 'KITÖLTÉS_ALATT',
  BACKOFFICE_REVIEW = 'ÜGYFÉLSZOLGÁLAT_ELLENŐRZÉSE_ALATT',
  FAILED = 'MEGHIÚSULT',
  ESTABLISHED = 'MEGALAPULT',
}

type StatusForSingleMemberFirmCreationType<T> =
  T extends SingleMemberFirmCreationType.SOLE_PROPRIETORSHIP
    ? SingleMemberFirmCreationStatus
    : T extends SingleMemberFirmCreationType.SINGLE_MEMBER_LIMITED_LIABILITY_COMPANY
      ? SingleMemberFirmCreationStatus
      : never;

export type GetSingleMemberFirmStatusesForDealsResponse<
  T extends SingleMemberFirmCreationType,
> = StatusForSingleMemberFirmCreationType<T>;

export type GetSingleMemberFirmDealsOrderableProps = Omit<
  GetSingleMemberFirmCreationsResponse,
  'type'
>;

export type GetSingleMemberFirmCreationsRequest = Partial<
  Order<GetSingleMemberFirmDealsOrderableProps>
> & {
  status?: SingleMemberFirmCreationStatus;
  type?: SingleMemberFirmCreationType;
};

export type SingleMemberFirmCreationsRequest = {
  id?: string;
  status?: SingleMemberFirmCreationStatus;
  type?: SingleMemberFirmCreationType;
};

export interface SingleMemberFirmCreationStatusChangeLog {
  id: string;
  oldStatus: SingleMemberFirmCreationStatus;
  newStatus: SingleMemberFirmCreationStatus;
  changedByUserId: string;
  createdAt: Date;
}

export interface SubmitSingleMemberFirmCreationRequest {
  type: SingleMemberFirmCreationType;
  pricingPackage: SingleMemberFirmPricingPackage;
  taxConsulting?: boolean;
  couponCode?: string;
  name: string;
  email: string;
  phoneNumber: string;
  acceptedGdpr: boolean;
  acceptedMarketing: boolean;
}

export interface EditSingleMemberFirmCreationRequest {
  personalDataSection?: SingleMemberFirmCreationPersonalDataSection;
  firmDataSection?: SingleMemberFirmDataSection;
  finalStepsSection?: Partial<SingleMemberFirmCreationFinalStepsSection>;
}

export interface PersistSingleMemberFirmCreationRequest {
  personalDataSection?: SingleMemberFirmCreationPersonalDataSection;
  firmDataSection?: SingleMemberFirmDataSection;
}

export interface SingleMemberFirmBackofficeCommentRequestBody {
  backofficeComment: string;
}

export interface SingleMemberFirmDateOfEntryRequestBody {
  dateOfEntry: Date;
}

export interface SingleMemberFirmEstablishedTaxNumberRequestBody {
  singleMemberFirmCreationId: string;
  establishedSingleMemberFirmTaxNumber: string;
}

export interface SingleMemberFirmCreationResponse {
  type: SingleMemberFirmCreationType;
  status: SingleMemberFirmCreationStatus;
  personalDataSection?: SingleMemberFirmCreationPersonalDataSection;
  firmDataSection?: SingleMemberFirmDataSection;
  dateOfEntry?: Date; // Bejegyyzés dátuma
  finalStepsSection?: SingleMemberFirmCreationFinalStepsSection;
  contactName: string | null;
  showEstablishedMessage: boolean | null;
}

export interface GetSingleMemberFirmCreationsResponse {
  id: string;
  status: SingleMemberFirmCreationStatus;
  identifier: string;
  customerName: string;
  companyName: string;
  updatedAt: Date;
  type: SingleMemberFirmCreationType;
}

export interface SingleMemberFirmCreationAdminResponse
  extends SingleMemberFirmCreationResponse {
  id: string;
  identifier: string;
  backofficeComment?: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  updatedAt: Date;
  statusChangeLogs: SingleMemberFirmStatusChangeLogAdminResponse[];
}

export interface SingleMemberFirmStatusChangeLogAdminResponse {
  oldStatus: SingleMemberFirmCreationStatus;
  newStatus: SingleMemberFirmCreationStatus;
  createdAt: Date;
}

export interface SingleMemberFirmCreationInitialDataSection {
  zipCode: string;
  fieldOfWork: SingleMemberFirmFieldsOfWork | string | null;
  typeOfTaxation: SingleMemberFirmTypesOfTaxation | null;
  vatCircle: boolean | null;
}

export interface SingleMemberFirmCreationPersonalDataSection {
  address?: SingleMemberFirmAddress | null;
  fieldOfWork?: SingleMemberFirmFieldsOfWork | null;
}

export interface SingleMemberFirmDataSection {
  estabilishedSingleMemberFirmTaxNumber?: string | null;
  typeOfTaxation?: SingleMemberFirmTypesOfTaxation | null;
  vatCircle?: boolean | null;
}

export interface SingleMemberFirmCreationFinalStepsSection {
  isBankAccountOpen: boolean | null;
  isAccountantFound: boolean | null;
  isKamaraRegistered: boolean | null;
  isKshLoggedIn: boolean | null;
  isNavLoggedIn: boolean | null;
  isHipaLoggedIn: boolean | null;
  isCegkapuRegistered: boolean | null;
}
